/**
 * Validates redirect URLs to prevent open redirect vulnerabilities (CodeQL SM01507)
 * Only allows relative URLs (same domain) or trusted Microsoft domains
 */

import TelemetryProvider from 'common/utilities/TelemetryProvider';

/**
 * List of trusted Microsoft domains that are allowed for redirects
 */
const TRUSTED_DOMAINS = ['mydefender.microsoft.com', 'mysecurity.microsoft.com', 'gb-ppe.microsoft.com'];

/**
 * Checks if a URL belongs to a trusted domain
 * @param url - The URL to check
 * @returns true if the URL is from a trusted domain, false otherwise
 */
const isTrustedDomain = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check if hostname exactly matches or is a subdomain of trusted domains
    return TRUSTED_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch {
    // Invalid URL
    return false;
  }
};

/**
 * Validates if a redirect URL is safe and allowed
 * @param url - The URL to validate (should be relative to current domain or a trusted domain)
 * @returns true if the URL is a safe relative URL or trusted domain, false otherwise
 */
export const isValidRedirectUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    TelemetryProvider.Instance.trackEvent('RedirectUrlValidation_InvalidInput', {
      url: typeof url,
    });
    return false;
  }

  const trimmedUrl = url.trim();

  // Empty string is valid (redirects to home)
  if (trimmedUrl === '') {
    TelemetryProvider.Instance.trackEvent('RedirectUrlValidation_EmptyUrl');
    return true;
  }

  // Reject protocol URLs at the start (e.g., javascript:, data:, file:, mailto:)
  // Check before relative URL logic to avoid false positives with query params like ?time=12:30
  const protocolMatch = trimmedUrl.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
  if (protocolMatch && !trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
    TelemetryProvider.Instance.trackEvent('RedirectUrlValidation_ProtocolBlocked', {
      url: trimmedUrl,
      protocol: protocolMatch[1],
    });
    return false;
  }

  // Reject protocol-relative URLs (//example.com) - these should never be used for redirects
  // This prevents bypasses when combined with slash-stripping logic elsewhere
  if (trimmedUrl.startsWith('//')) {
    TelemetryProvider.Instance.trackEvent('RedirectUrlValidation_ProtocolRelativeBlocked', {
      url: trimmedUrl,
    });
    return false;
  }

  // Check if it's an absolute URL
  const isAbsoluteUrl = trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://');

  if (isAbsoluteUrl) {
    // Allow if it's a trusted Microsoft domain
    return isTrustedDomain(trimmedUrl);
  }

  // Reject URLs with backslashes (could bypass validation)
  if (trimmedUrl.includes('\\')) {
    TelemetryProvider.Instance.trackEvent('RedirectUrlValidation_BackslashBlocked', {
      url: trimmedUrl,
    });
    return false;
  }

  // At this point, we have a relative URL (same domain)
  // This is safe for redirection
  return true;
};

/**
 * Sanitizes a redirect URL by validating it and returning a safe fallback if invalid
 * @param url - The URL to sanitize
 * @param fallbackUrl - The URL to return if validation fails (default: '')
 * @returns A safe URL for redirection
 */
export const sanitizeRedirectUrl = (url: string, fallbackUrl = ''): string => {
  if (isValidRedirectUrl(url)) {
    return url;
  }

  // Log invalid redirect attempt for security monitoring
  TelemetryProvider.Instance.trackEvent('Invalid redirect URL blocked: ', { url });
  console.warn('Invalid redirect URL blocked:', url);

  return fallbackUrl;
};
